import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Gift, CheckCircle2, Loader2, Palette, Puzzle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import SectionHeading from "@/components/SectionHeading";
import { subscribe } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PERKS = [
  { icon: Palette, text: "Printable coloring sheets of every fable hero" },
  { icon: Puzzle, text: "Jungle mazes, counting trails & matching games" },
  { icon: Sparkles, text: "Moral-of-the-story cards for bedtime talks" },
];

export default function LeadMagnet() {
  const [form, setForm] = useState({ name: "", email: "", child_age: "4–6" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = "Please tell us your name";
    if (!EMAIL_RE.test(form.email)) er.email = "Please enter a valid email address";
    setErrors(er);
    if (Object.keys(er).length) return;
    setStatus("loading");
    try {
      const res = await subscribe(form);
      setStatus("done");
      toast.success(
        res.status === "exists"
          ? "You're already on the list — enjoy the pack again!"
          : "Welcome to the Banyan family!"
      );
    } catch {
      setStatus("idle");
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="activity-pack"
      data-testid="activity-pack-section"
      className="relative overflow-hidden bg-parchment py-24 text-charcoal lg:py-32"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(800px 480px at 8% 100%, rgba(212,175,55,0.16), transparent 60%)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid overflow-hidden rounded-sm border border-emerald/15 bg-cream shadow-lift lg:grid-cols-2">
          <div className="relative flex flex-col justify-center gap-6 p-8 sm:p-12 lg:p-16">
            <SectionHeading
              testId="lead-magnet-heading"
              eyebrow="Free Gift for Little Readers"
              title={
                <>
                  The Banyan Tree{" "}
                  <em className="text-emerald">Coloring & Activity Pack.</em>
                </>
              }
              sub="A 28-page printable PDF packed with fable mazes, coloring sheets, and moral matching games — the perfect companion to the series, free for every family."
            />
            <ul className="space-y-4">
              {PERKS.map(({ icon: Icon, text }) => (
                <motion.li
                  key={text}
                  initial={{ opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, ease: EASE }}
                  className="flex items-center gap-3 text-sm text-charcoal/75"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-gold/10 text-emerald">
                    <Icon className="h-4 w-4" />
                  </span>
                  {text}
                </motion.li>
              ))}
            </ul>
          </div>

          <div className="relative flex items-center bg-emerald p-8 sm:p-12 lg:p-16">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(420px 320px at 80% 10%, rgba(212,175,55,0.16), transparent 65%)",
              }}
              aria-hidden="true"
            />
            <AnimatePresence mode="wait">
              {status === "done" ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="relative w-full text-center"
                  data-testid="lead-magnet-success"
                >
                  <CheckCircle2 className="mx-auto h-14 w-14 text-gold" aria-hidden="true" />
                  <h3 className="mt-6 font-serif text-3xl font-semibold text-cream">
                    Welcome to the Banyan family{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                  </h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-cream/70">
                    Your free Activity Pack is ready. We'll also write to you when the
                    next fable in the 50-book roadmap leaves the press.
                  </p>
                  <a
                    href="/banyan-activity-pack.pdf"
                    download
                    data-testid="lead-magnet-download-btn"
                    className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow"
                  >
                    <Download className="h-4 w-4" />
                    Download the Pack (PDF)
                  </a>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  onSubmit={submit}
                  noValidate
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="relative w-full"
                  data-testid="lead-magnet-form"
                >
                  <div className="flex items-center gap-3">
                    <Gift className="h-5 w-5 text-gold" aria-hidden="true" />
                    <h3 className="font-serif text-2xl font-semibold text-cream">
                      Get your free pack
                    </h3>
                  </div>
                  <p className="mt-2 text-sm text-cream/65">
                    Instant download · No spam, ever · Unsubscribe anytime
                  </p>

                  <div className="mt-8 space-y-5">
                    <div>
                      <label htmlFor="lm-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-gold/85">
                        Parent's Name
                      </label>
                      <input
                        id="lm-name"
                        type="text"
                        value={form.name}
                        onChange={set("name")}
                        data-testid="lead-magnet-name-input"
                        aria-invalid={!!errors.name}
                        placeholder="Maya Sharma"
                        className="w-full rounded-sm border border-cream/20 bg-emerald-deep/70 px-4 py-3.5 text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none"
                      />
                      {errors.name && (
                        <p className="mt-1.5 text-xs text-gold-amber" role="alert" data-testid="lead-magnet-name-error">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="lm-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-gold/85">
                        Email Address
                      </label>
                      <input
                        id="lm-email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        data-testid="lead-magnet-email-input"
                        aria-invalid={!!errors.email}
                        placeholder="you@example.com"
                        className="w-full rounded-sm border border-cream/20 bg-emerald-deep/70 px-4 py-3.5 text-cream placeholder:text-cream/35 focus:border-gold focus:outline-none"
                      />
                      {errors.email && (
                        <p className="mt-1.5 text-xs text-gold-amber" role="alert" data-testid="lead-magnet-email-error">
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="lm-age" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.24em] text-gold/85">
                        Child's Age
                      </label>
                      <select
                        id="lm-age"
                        value={form.child_age}
                        onChange={set("child_age")}
                        data-testid="lead-magnet-age-select"
                        className="w-full rounded-sm border border-cream/20 bg-emerald-deep/70 px-4 py-3.5 text-cream focus:border-gold focus:outline-none"
                      >
                        <option value="4–6">4–6 years</option>
                        <option value="6–8">6–8 years</option>
                        <option value="8–11">8–11 years</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid="lead-magnet-submit-btn"
                      className="flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-emerald-ink transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-glow disabled:opacity-60"
                    >
                      {status === "loading" ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <Download className="h-4 w-4" aria-hidden="true" />
                      )}
                      Send My Free Pack
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
